import { Color } from '../math/Color';
import { Vector2 } from '../math/Vector2';

/** Field values a {@link SpriteRenderer} can be built with. Omitted fields keep their default. */
export interface SpriteRendererOptions {
  /** Asset key of the texture to draw. */
  readonly texture?: string;
  /** Tint, or fill color when there is no texture. */
  readonly color?: Color;
  /** Size in world units. */
  readonly size?: Vector2;
  /** Pivot as a fraction of the size. */
  readonly anchor?: Vector2;
  /** Draw order; higher values draw in front. */
  readonly sortOrder?: number;
  /** Whether the sprite is drawn at all. */
  readonly visible?: boolean;
}

const DEFAULT_SIZE = 100;
const CENTER_ANCHOR = 0.5;

/**
 * Makes an entity visible as a flat, camera-facing quad.
 *
 * Pure data, like every component: the render system reads these fields each frame and turns them into
 * a draw call. Nothing here holds a texture object or talks to the renderer.
 *
 * The texture is an **asset key**, not a `Texture`. A key stays serializable, survives a round trip
 * through the editor and lets the asset layer own loading and caching, which an object reference on a
 * component could not.
 */
export class SpriteRenderer {
  /** Asset key of the texture, or undefined to draw an untextured quad in {@link SpriteRenderer.color}. */
  texture: string | undefined;
  /** Tint multiplied into the texture, or the fill color when there is none. */
  readonly color: Color;
  /** Size in world units, before the entity's world scale is applied. */
  readonly size: Vector2;
  /** Pivot as a fraction of the size: `(0.5, 0.5)` centers the sprite on the entity's position. */
  readonly anchor: Vector2;
  /** Draw order; higher values draw in front. Ties are broken by entity id, so order is stable. */
  sortOrder: number;
  /** Whether the sprite is drawn. A hidden sprite costs nothing beyond the visibility check. */
  visible: boolean;

  constructor(options: SpriteRendererOptions = {}) {
    this.texture = options.texture;
    this.color = options.color ?? Color.white();
    this.size = options.size ?? new Vector2(DEFAULT_SIZE, DEFAULT_SIZE);
    this.anchor = options.anchor ?? new Vector2(CENTER_ANCHOR, CENTER_ANCHOR);
    this.sortOrder = options.sortOrder ?? 0;
    this.visible = options.visible ?? true;
  }
}
